import React, { useRef } from "react";
import moment from "moment";
import CalendarArtistAvatar from "./CalendarArtistAvatar";

// =======================================================
// MobileCalendarSidebar
// =======================================================

const MobileCalendarSidebar = ({
  sidebarMonth,
  availableMonthKeys = [],
  mobileSidebar = {},
  artistProfiles = {},
  activeDayId,
  onChangeMonth,
  onSelectMonth,
  onScrollToDay,
  getStatusClass,
}) => {
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const todayKey = moment().format("YYYY-MM-DD");

  // =====================================================
  // SWIPE MONTH NAVIGATION
  // Swipe left  = next month
  // Swipe right = previous month
  // =====================================================

  const handleTouchStart = (event) => {
    const touch = event.touches?.[0];

    if (!touch) return;

    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
  };

  const handleTouchEnd = (event) => {
    if (
      touchStartX.current === null ||
      touchStartY.current === null
    ) {
      return;
    }

    const touch = event.changedTouches?.[0];

    if (!touch) return;

    const diffX =
      touch.clientX - touchStartX.current;

    const diffY =
      touch.clientY - touchStartY.current;

    touchStartX.current = null;
    touchStartY.current = null;

    // Vertical gesture -> normal page scrolling
    if (Math.abs(diffY) > Math.abs(diffX)) {
      return;
    }

    // Ignore tiny accidental horizontal movements
    if (Math.abs(diffX) < 45) {
      return;
    }

    if (diffX < 0) {
      // Swipe left
      onChangeMonth?.(1);
    } else {
      // Swipe right
      onChangeMonth?.(-1);
    }
  };

  // =====================================================
  // MONTHS
  // =====================================================

  const currentMonthIndex =
    availableMonthKeys.indexOf(sidebarMonth);

  const monthOffsets = [-1, 0, 1];

  // =====================================================
  // ARTISTS
  // Same alphabetical sorting as Booking View
  // =====================================================

  const sortedArtists = Object.entries(
    mobileSidebar
  ).sort(([artistA], [artistB]) =>
    artistA.localeCompare(artistB)
  );

  return (
    <aside className="mobile-sidebar">

      {/* ===============================================
          TITLE
          =============================================== */}

      <div className="mobile-sidebar-title">
        Künstler
      </div>

      {/* ===============================================
          MONTH CAROUSEL
          =============================================== */}

      <div
        className="sidebar-month-carousel"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {monthOffsets.map((offset) => {
          const monthIndex =
            currentMonthIndex + offset;

          const monthKey =
            availableMonthKeys[monthIndex];

          // Keep the 3-column layout even when
          // there is no previous / next month.
          if (!monthKey) {
            return (
              <div
                key={`empty-${offset}`}
                className="sidebar-month-item empty"
              />
            );
          }

          const isCurrent = offset === 0;

          return (
            <button
              key={monthKey}
              type="button"
              className={[
                "sidebar-month-item",
                isCurrent ? "active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() =>
                onSelectMonth?.(monthKey)
              }
            >
              {moment(
                monthKey,
                "YYYY-MM"
              )
                .format(
                  isCurrent
                    ? "MMM YY"
                    : "MMM"
                )
                .toUpperCase()}
            </button>
          );
        })}
      </div>

      {/* ===============================================
          ARTISTS
          =============================================== */}

      {sortedArtists.map(
        ([artistName, dates]) => (
          <div
            key={artistName}
            className="sidebar-artist"
          >
            <div className="sidebar-name">
              <CalendarArtistAvatar name={artistName} src={artistProfiles[dates[0]?.artistId]} />
              <span>{artistName}</span>
            </div>

            {/* =========================================
                DAYS
                ========================================= */}

            <div className="sidebar-days-grid">
              {[...(dates || [])]
                .sort((a, b) =>
                  moment(a.day).diff(
                    moment(b.day)
                  )
                )
                .map((date) => {
                  const dateKey =
                    moment(date.day).format(
                      "YYYY-MM-DD"
                    );

                  const isToday =
                    dateKey === todayKey;

                  const isActive =
                    String(activeDayId) ===
                    String(date.dayId);

                  const statusClass =
                    getStatusClass?.(
                      date.bookingStatus
                    ) || "";

                  return (
                    <button
                      key={date.dayId}
                      type="button"
                      className={[
                        "sidebar-date",
                        statusClass,
                        isToday
                          ? "is-today"
                          : "",
                        isActive
                          ? "is-active"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() =>
                        onScrollToDay?.(
                          date.dayId
                        )
                      }
                      title={moment(
                        date.day
                      ).format(
                        "DD.MM.YYYY"
                      )}
                    >
                      <span className="sidebar-date-weekday">
                        {moment(
                          date.day
                        )
                          .format("dd")
                          .toUpperCase()}
                      </span>

                      <span className="sidebar-date-number">
                        {moment(
                          date.day
                        ).format("DD")}
                      </span>
                    </button>
                  );
                })}
            </div>
          </div>
        )
      )}
    </aside>
  );
};

export default MobileCalendarSidebar;